// src/components/UserStoryPointChart.tsx
import React, { useState, useMemo } from 'react';
import { useAtom } from 'jotai';
import { sprintsAtom, userAllocationsAtom, projectsAtom } from '@/lib/store';
import { Issue } from '@/types';
import Tooltip from './Tooltip';
import { createProjectMap, getProjectBreakdown, ProjectInfo } from '@/utils/projectUtils';
import { getMaxStoryPoints, getProjectTotals } from '@/utils/chartUtils';
import { useProjectHighlight } from '@/hooks/useProjectHighlight';
import { getTargetPriority } from '@/utils/sprintTarget';

interface UserStoryPointChartProps {
  viewMode?: 'user' | 'project';
  hideToggle?: boolean;
}

interface UserWorkload {
  userId: string;
  userName: string;
  totalPoints: number;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  content: React.ReactNode | null;
}

export default function UserStoryPointChart({ viewMode = 'user', hideToggle = false }: UserStoryPointChartProps) {
  const [sprints] = useAtom(sprintsAtom);
  const [userAllocations] = useAtom(userAllocationsAtom);
  const [projects] = useAtom(projectsAtom);
  const [localViewMode] = useState<'user' | 'project'>(viewMode);
  const activeViewMode = hideToggle ? viewMode : localViewMode;
  const {
    highlightedProject,
    highlightProject: setHighlightProject,
    clearHighlight,
    isUserHighlighted
  } = useProjectHighlight();
  const [tooltipData, setTooltipData] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    content: null
  });

  // Calculate total story points per user across all sprints to identify overloaded users
  const userTotalPoints = useMemo<UserWorkload[]>(() => {
    return userAllocations
      .map(allocation => ({
        userId: allocation.user.id,
        userName: allocation.user.displayName,
        totalPoints: allocation.totalStoryPoints
      }))
      .sort((a, b) => b.totalPoints - a.totalPoints);
  }, [userAllocations]);

  // Get the top 3 users with most story points
  const topOverloadedUsers = useMemo<Set<string>>(() => {
    const top3 = userTotalPoints.slice(0, 3);
    return new Set(top3.map(user => user.userId));
  }, [userTotalPoints]);

  // Extract all unique projects from issue keys and filter out projects with no story points
  const projectMap = useMemo<Map<string, ProjectInfo>>(() => {
    // Collect all issues from all users and sprints
    const allIssues: Issue[] = [];
    userAllocations.forEach(allocation => {
      Object.values(allocation.sprints).forEach(sprintData => {
        allIssues.push(...sprintData.issues);
      });
    });

    return createProjectMap(allIssues, projects);
  }, [userAllocations, projects]);

  // Find the maximum story points in any sprint for any user
  const maxStoryPoints = useMemo<number>(() =>
    getMaxStoryPoints(userAllocations),
    [userAllocations]
  );

  // Common tooltip event handlers
  const handleTooltipEnter = (content: React.ReactNode) => (e: React.MouseEvent) => {
    setTooltipData({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      content
    });
  };

  const handleTooltipMove = (e: React.MouseEvent) => {
    if (tooltipData.visible) {
      setTooltipData({
        ...tooltipData,
        x: e.clientX,
        y: e.clientY
      });
    }
  };

  const handleTooltipLeave = () => {
    setTooltipData({...tooltipData, visible: false});
  };

  // Project highlighting handlers
  const highlightProjectHandler = (projectKey: string) => () => {
    setHighlightProject(projectKey, userAllocations);
  };

  // Render overload indicator badge
  const renderOverloadBadge = (userId: string) => {
    const rank = userTotalPoints.findIndex(u => u.userId === userId);
    if (rank < 0 || rank > 2) return <></>;

    const badgeStyles = [
      "inline-flex items-center justify-center w-5 h-5 bg-gradient-to-br from-yellow-300 to-orange-500 text-white text-xs font-bold rounded-full shadow-md animate-pulse",
      "inline-flex items-center justify-center w-5 h-5 bg-gradient-to-br from-yellow-400 to-orange-600 text-white text-xs font-bold rounded-full shadow-sm",
      "inline-flex items-center justify-center w-5 h-5 bg-gradient-to-br from-orange-400 to-orange-700 text-white text-xs font-bold rounded-full"
    ];

    return (
      <div className="ml-2 relative" title={`Top ${rank + 1} overloaded user`}>
        <span className={badgeStyles[rank]}>
          {rank + 1}
        </span>
      </div>
    );
  };

  const chartHeight = userAllocations.length * 40 + 50; // 40px per user + 50px for labels

  if (sprints.length === 0 || userAllocations.length === 0) {
    return <></>;
  }

  return (
    <div className="relative">
      <div className="flex h-full overflow-x-auto gap-6" style={{height: `${chartHeight}px`}}>
        {/* Y-axis (Users) - Sticky column */}
        <div className="flex flex-col justify-around sticky left-0 z-10 bg-white dark:bg-gray-800 pr-6">
          <div className={`h-8 text-sm font-bold text-black dark:text-white`}>Users</div>
          {userAllocations.map((allocation) => {
            const isHighlighted = isUserHighlighted(allocation.user.id);
            return (
              <div
                key={allocation.user.id}
                className={`h-8 flex items-center transition-opacity duration-200 ${isHighlighted ? 'opacity-100' : 'opacity-30'}`}
              >
                {allocation.user.avatarUrl && (
                    <img
                        src={allocation.user.avatarUrl}
                        alt={allocation.user.displayName}
                        className={`w-6 h-6 rounded-full mr-2 ${isHighlighted && highlightedProject !== null ? 'ring-1 ring-primary' : ''}`}
                    />
                )}
                <div className="flex items-center">
                  <span className="truncate text-black dark:text-white">{allocation.user.displayName}</span>
                  {topOverloadedUsers.has(allocation.user.id) && renderOverloadBadge(allocation.user.id)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Chart area - Horizontally scrollable */}
        <div className="h-full flex gap-5">
          {sprints.map((sprint) => (
              <div
                  key={sprint.id}
                  className="flex flex-col justify-around"
                  style={{ minWidth: '200px' }}
              >
                {/* Sprint name at the top */}
                <div className="text-sm h-8 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <div className={`font-bold text-black dark:text-white`}>{sprint.name}</div>
                    {getTargetPriority(sprint.target) === 'high' && (
                      <svg 
                        className="w-3 h-3 text-red-500 cursor-pointer" 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                        onMouseEnter={handleTooltipEnter(
                          <div className="p-2 rounded-md border text-sm border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                            <div className="flex items-start gap-2">
                              <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              <div className="flex-1 leading-tight whitespace-pre-wrap">{sprint.target}</div>
                            </div>
                          </div>
                        )}
                        onMouseMove={handleTooltipMove}
                        onMouseLeave={handleTooltipLeave}
                      >
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    )}
                    {getTargetPriority(sprint.target) === 'normal' && (
                      <svg 
                        className="w-3 h-3 text-blue-500 cursor-pointer" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                        onMouseEnter={handleTooltipEnter(
                          <div className="p-2 rounded-md border text-sm border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                            <div className="flex items-start gap-2">
                              <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="flex-1 leading-tight whitespace-pre-wrap">{sprint.target}</div>
                            </div>
                          </div>
                        )}
                        onMouseMove={handleTooltipMove}
                        onMouseLeave={handleTooltipLeave}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
                  </div>
                </div>
                {userAllocations.map((allocation) => {
                  const sprintData = allocation.sprints[sprint.id] || {
                    storyPoints: 0,
                    completedStoryPoints: 0,
                    qaStoryPoints: 0,
                    issues: []
                  };

                  if (activeViewMode === 'user') {
                    // User view - show completed vs total
                    const totalPercentage = maxStoryPoints > 0 ? (sprintData.storyPoints / maxStoryPoints) * 100 : 0;
                    const completedPercentage = maxStoryPoints > 0 ? (sprintData.completedStoryPoints / maxStoryPoints) * 100 : 0;
                    const qaPercentage = maxStoryPoints > 0 ? (sprintData.qaStoryPoints / maxStoryPoints) * 100 : 0;

                    const userTooltipContent = (
                      <div className="tooltip-content">
                        <div className="font-bold">{allocation.user.displayName}</div>
                        <div>Sprint: {sprint.name}</div>
                        <div>Completed: {sprintData.completedStoryPoints} points</div>
                        <div>QA/Ready to Deploy: {sprintData.qaStoryPoints} points</div>
                        <div>Total: {sprintData.storyPoints} points</div>
                        <div>Progress: {sprintData.storyPoints > 0 ?
                          Math.round((sprintData.completedStoryPoints / sprintData.storyPoints) * 100) : 0}%</div>
                      </div>
                    );

                    return (
                        <div key={`${sprint.id}-${allocation.user.id}`} className="h-8 flex items-center">
                          <div className="flex items-center w-full">
                            <div
                              className="relative flex-1 h-4 cursor-pointer"
                              onMouseEnter={handleTooltipEnter(userTooltipContent)}
                              onMouseMove={handleTooltipMove}
                              onMouseLeave={handleTooltipLeave}
                            >
                              {/* Total story points bar */}
                              <div
                                  className="absolute left-0 top-0 h-full bg-primary rounded"
                                  style={{
                                    width: `${totalPercentage-qaPercentage-completedPercentage}%`,
                                    left: `${qaPercentage+completedPercentage}%`
                                  }}
                              ></div>

                              {/* QA story points bar */}
                              <div
                                  className="absolute top-0 h-full bg-yellow-400 rounded"
                                  style={{ width: `${qaPercentage}%`, left: `${completedPercentage}%` }}
                              ></div>

                              {/* Completed story points bar */}
                              <div
                                  className="absolute left-0 top-0 h-full bg-green-400 rounded"
                                  style={{ width: `${completedPercentage}%` }}
                              ></div>
                            </div>
                            {/* Story point sums displayed next to the bar */}
                            <div className="ml-2 text-xs text-black dark:text-white">
                              {sprintData.completedStoryPoints}/{sprintData.storyPoints}
                            </div>
                          </div>
                        </div>
                    );
                  } else {
                    // Project view - show breakdown by project
                    const projectBreakdown = getProjectBreakdown(sprintData.issues);
                    let currentLeft = 0;

                    return (
                        <div key={`${sprint.id}-${allocation.user.id}`} className="h-8 flex items-center">
                          <div className="relative w-full h-4">
                            {/* Render bars for each project, sorted by story points (descending) */}
                            {Array.from(projectBreakdown.entries())
                              .sort((a, b) => b[1].storyPoints - a[1].storyPoints)
                              .map(([projectKey, projectData]) => {
                              const projectPercentage = maxStoryPoints > 0 ?
                                  (projectData.storyPoints / maxStoryPoints) * 100 : 0;

                              const left = currentLeft;
                              currentLeft += projectPercentage;

                              const projectInfo = projectMap.get(projectKey);
                              const isHighlighted = highlightedProject === projectKey;
                              const isDimmed = highlightedProject !== null && highlightedProject !== projectKey;

                              const totals = getProjectTotals(projectKey, userAllocations);
                              const projectTooltipContent = (
                                <div className="tooltip-content">
                                  <div className="font-bold">{projectKey}</div>
                                  {projectInfo?.name !== projectKey && (
                                    <div className="text-sm text-gray-500">{projectInfo?.name}</div>
                                  )}
                                  <div>User: {allocation.user.displayName}</div>
                                  <div>Sprint: {sprint.name}</div>
                                  <div>Points: {projectData.completedStoryPoints}/{projectData.storyPoints}</div>
                                  <div className="mt-2 pt-1 border-t border-gray-200">
                                    <div className="font-bold">Project Totals:</div>
                                    <div>Points: {totals.completed}/{totals.total}</div>
                                  </div>
                                </div>
                              );

                              return (
                                  <div
                                      key={`${sprint.id}-${allocation.user.id}-${projectKey}`}
                                      className={`absolute top-0 h-full ${projectInfo?.color || 'bg-gray-400'} cursor-pointer rounded transition-opacity duration-200 ${isHighlighted ? 'ring-2 ring-white z-10' : ''} ${isDimmed ? 'opacity-30' : 'opacity-100'}`}
                                      style={{
                                        left: `${left}%`,
                                        width: `${projectPercentage}%`
                                      }}
                                      onMouseEnter={(e) => {
                                        highlightProjectHandler(projectKey)();
                                        handleTooltipEnter(projectTooltipContent)(e);
                                      }}
                                      onMouseMove={(e) => {
                                        // Recalculate tooltip content on each move to ensure it updates
                                        const totals = getProjectTotals(projectKey, userAllocations);
                                        const updatedContent = (
                                          <div className="tooltip-content">
                                            <div className="font-bold">{projectKey}</div>
                                            {projectInfo?.name !== projectKey && (
                                              <div className="text-sm text-gray-500">{projectInfo?.name}</div>
                                            )}
                                            <div>User: {allocation.user.displayName}</div>
                                            <div>Sprint: {sprint.name}</div>
                                            <div>Points: {projectData.completedStoryPoints}/{projectData.storyPoints}</div>
                                            <div className="mt-2 pt-1 border-t border-gray-200">
                                              <div className="font-bold">Project Totals:</div>
                                              <div>Points: {totals.completed}/{totals.total}</div>
                                            </div>
                                          </div>
                                        );
                                        setTooltipData({
                                          visible: true,
                                          x: e.clientX,
                                          y: e.clientY,
                                          content: updatedContent
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        clearHighlight();
                                        handleTooltipLeave();
                                      }}
                                  ></div>
                              );
                            })}
                          </div>
                        </div>
                    );
                  }
                })}
              </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-end mt-2 mb-2 text-xs overflow-x-auto">
        {activeViewMode === 'user' ? (
            <>
              <div className="flex items-center mr-4">
                <div className="w-3 h-3 bg-primary mr-1"></div>
                <span className="text-black dark:text-white">Total Story Points</span>
              </div>
              <div className="flex items-center mr-4">
                <div className="w-3 h-3 bg-green-400 mr-1"></div>
                <span className="text-black dark:text-white">Completed Story Points</span>
              </div>
              <div className="flex items-center mr-4">
                <div className="w-3 h-3 bg-yellow-400 mr-1"></div>
                <span className="text-black dark:text-white">QA/Ready to Deploy</span>
              </div>
              <div className="flex items-center">
                <div className="flex space-x-1 mr-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-gradient-to-br from-yellow-300 to-orange-500 text-white text-xs font-bold rounded-full shadow-md">1</span>
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-gradient-to-br from-yellow-400 to-orange-600 text-white text-xs font-bold rounded-full shadow-sm">2</span>
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-gradient-to-br from-orange-400 to-orange-700 text-white text-xs font-bold rounded-full">3</span>
                </div>
                <span className="text-black dark:text-white">Top Overloaded Users</span>
              </div>
            </>
        ) : (
            // Project legend with hover capability, sorted alphabetically
            Array.from(projectMap.values())
              .sort((a, b) => a.key.localeCompare(b.key))
              .map(project => {
                const totals = getProjectTotals(project.key, userAllocations);
                const legendTooltipContent = (
                  <div className="tooltip-content">
                    <div className="font-bold">{project.key}</div>
                    {project.name !== project.key && (
                      <div className="text-sm text-gray-500">{project.name}</div>
                    )}
                    <div>Total Points: {totals.completed}/{totals.total}</div>
                  </div>
                );

                return (
                  <div
                    key={project.key}
                    className={`flex items-center mr-4 mb-1 px-2 py-1 rounded cursor-pointer transition-colors duration-200 ${highlightedProject === project.key ? 'bg-gray-200 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    onMouseEnter={(e) => {
                      highlightProjectHandler(project.key)();
                      handleTooltipEnter(legendTooltipContent)(e);
                    }}
                    onMouseMove={(e) => {
                      // Recalculate tooltip content on each move to ensure it updates
                      const totals = getProjectTotals(project.key, userAllocations);
                      const updatedContent = (
                        <div className="tooltip-content">
                          <div className="font-bold">{project.key}</div>
                          {project.name !== project.key && (
                            <div className="text-sm text-gray-500">{project.name}</div>
                          )}
                          <div>Total Points: {totals.completed}/{totals.total}</div>
                        </div>
                      );
                      setTooltipData({
                        visible: true,
                        x: e.clientX,
                        y: e.clientY,
                        content: updatedContent
                      });
                    }}
                    onMouseLeave={() => {
                      clearHighlight();
                      handleTooltipLeave();
                    }}
                  >
                    <div className={`w-3 h-3 ${project.color} mr-1 ${highlightedProject === project.key ? 'ring-1 ring-gray-600' : ''}`}></div>
                    <span className="text-black dark:text-white">{project.key}</span>
                  </div>
                );
              })
        )}
      </div>

      {/* Tooltip */}
      <Tooltip
        visible={tooltipData.visible}
        x={tooltipData.x}
        y={tooltipData.y}
        content={tooltipData.content}
      />
    </div>
  );
}
